import { IDiscussionGuideQuestion } from '../models/data-capture.models';

export type QuestionWithNotation<
  T extends IDiscussionGuideQuestion = IDiscussionGuideQuestion,
> = T & {
  depth: number;
  numberNotation: string;
};

const ROOT_KEY = '__root__';

export const orderQuestionsForDisplay = <T extends IDiscussionGuideQuestion>(
  questions: T[]
): T[] => {
  const questionIds = new Set(
    questions.map((question) => question.question_id)
  );

  const questionsByParent = questions.reduce<Record<string, T[]>>(
    (acc, question) => {
      const parentKey =
        question.parent_question_id &&
        questionIds.has(question.parent_question_id)
          ? question.parent_question_id
          : ROOT_KEY;

      return {
        ...acc,
        [parentKey]: [...(acc[parentKey] || []), question],
      };
    },
    {}
  );

  const traverse = (parentKey: string): T[] =>
    (questionsByParent[parentKey] || []).flatMap((question) => [
      question,
      ...traverse(question.question_id),
    ]);

  return traverse(ROOT_KEY);
};

export const assignDepthAndNotation = <T extends IDiscussionGuideQuestion>(
  questions: T[]
): QuestionWithNotation<T>[] => {
  const questionMap = new Map<string, QuestionWithNotation<T>>();

  questions.forEach((q) =>
    questionMap.set(q.question_id, { ...q, depth: 0, numberNotation: '' })
  );

  const counters: number[] = [];
  const result: QuestionWithNotation<T>[] = [];

  for (const q of questions) {
    let depth = 0;

    if (q.parent_question_id) {
      const parent = questionMap.get(q.parent_question_id);

      depth = parent ? parent.depth + 1 : 0;
    }

    while (counters.length <= depth) {
      counters.push(0);
    }

    counters[depth]++;

    for (let i = depth + 1; i < counters.length; i++) {
      counters[i] = 0;
    }

    const notationParts = counters
      .slice(0, depth + 1)
      .map((count, idx) => formatNotationSegment(idx, count - 1));

    const newQ: QuestionWithNotation<T> = {
      ...q,
      depth,
      numberNotation: notationParts.join(''),
    };

    questionMap.set(q.question_id, newQ);

    result.push(newQ);
  }

  return result;
};

export const formatNotationSegment = (depth: number, index: number) => {
  const formats = [toNumeric, toLetter, toRoman];

  const fn = formats[depth % formats.length];

  return fn(index + 1);
};

const toNumeric = (n: number) => `${n}`;

const toLetter = (n: number) => String.fromCharCode(96 + n);

const toRoman = (n: number) => {
  const map: [number, string][] = [
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
  ];

  return map.reduce<string>((result, [value, numeral]) => {
    const count = Math.floor(n / value);
    n = n % value;
    return result + numeral.repeat(count);
  }, '');
};
